import React, { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { useGiphySearch } from '../../hooks/api';

const GifPicker = ({ onSelect, disabled = false }) => {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { data = [], isLoading, isError, error } = useGiphySearch(debounced, true);
  const errorDetail = error?.response?.data?.detail;

  return (
    <div className="flex flex-col" data-testid="gif-picker">
      <div className="px-2 pt-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-secondary-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Giphy…"
            disabled={disabled}
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-secondary-200 bg-secondary-50 text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-secondary-600 dark:bg-secondary-900 dark:text-secondary-100"
            data-testid="gif-search-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5 p-2 h-72 min-h-[18rem] max-h-80 overflow-y-auto content-start">
        {isLoading && (
          <div className="col-span-3 flex items-center justify-center gap-2 py-8 text-sm text-secondary-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading GIFs…
          </div>
        )}
        {isError && !isLoading && (
          <div className="col-span-3 px-2 py-6 text-center text-xs text-secondary-500 dark:text-secondary-400">
            {typeof errorDetail === 'string'
              ? errorDetail
              : 'Could not load GIFs. Check Giphy configuration.'}
          </div>
        )}
        {!isLoading && !isError && data.length === 0 && (
          <div className="col-span-3 px-2 py-6 text-center text-xs text-secondary-500">
            No GIFs found
          </div>
        )}
        {!isLoading &&
          !isError &&
          data.map((gif) => (
            <button
              key={gif.id}
              type="button"
              disabled={disabled}
              onClick={() => onSelect?.(gif)}
              className="relative h-28 w-full overflow-hidden rounded-md bg-secondary-100 dark:bg-secondary-700 hover:ring-2 hover:ring-primary-500 disabled:opacity-50"
              aria-label={gif.title || 'Send GIF'}
            >
              <img
                src={gif.preview_url || gif.url}
                alt={gif.title || 'GIF'}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </button>
          ))}
      </div>
      <p className="px-2 pb-1.5 text-[10px] text-secondary-400 text-right">
        Powered by Giphy
      </p>
    </div>
  );
};

export default GifPicker;
